/**
 Copyright (c) 2015, 2018, Oracle and/or its affiliates.
 The Universal Permissive License (UPL), Version 1.0
 */

'use strict';

define('vb/components/mobile-page-template/viewModel',[
  'ojs/ojthemeutils',
], (ThemeUtils) => {
  class PageTemplateComponentModel {
    constructor(context) {
      this.composite = context.element;
      this.configure();
    }

    /**
     * Configures the composite component
     */
    configure() {
      // Add a mobile app specific marker on the body
      document.body.classList.add('oj-ext-mobile-app');

      // header is bigger in android so set a marker style for android theme
      const themeTargetPlatform = ThemeUtils.getThemeTargetPlatform();
      if (themeTargetPlatform === 'android') {
        this.composite.classList.add('oj-ext-android-theme');
      } else if (themeTargetPlatform === 'ios') {
        this.composite.classList.add('oj-ext-ios-theme');
      }
    }

    // Composite lifecycle listener
    bindingsApplied() {
      const pageNavbar = this.composite.getElementsByClassName('oj-hybrid-applayout-navbar-page')[0];
      if (pageNavbar.children.length > 0) {
        // page navbar has children so replace the border class on the header
        const header = this.composite.getElementsByTagName('header')[0];
        header.classList.remove('oj-hybrid-applayout-header');
        header.classList.add('oj-hybrid-applayout-header-no-border');
        // add a marker class so that the page content can have more margins
        this.composite.classList.add('oj-ext-mobile-page-with-navbar');
      } else {
        // hide the page navbar if it is empty
        pageNavbar.style.display = 'none';
      }

      const statusTapListener = this._statusTapListener.bind(this);
      const fixedTop = this.composite.getElementsByClassName('oj-applayout-fixed-top')[0];
      if (fixedTop) {
        fixedTop.addEventListener('click', (e) => {
          const { className } = e.target;
          // eslint-disable-next-line max-len
          const regex = /oj-hybrid-applayout-header|oj-hybrid-applayout-bar-title|oj-ex-mobile-header-content|oj-hybrid-applayout-header-title/;
          // only enable direct click on the header/or the title. i.e. ignore click on any other elements inside
          if (className.match(regex)) {
            statusTapListener();
          }
        });
      }
      window.addEventListener('statusTap', statusTapListener);
    }

    /**
     * Listener for scrolling the page content to the top on 'tap' of the status area
     * @private
     */
    _statusTapListener() {
      const contentElement = this.composite.getElementsByClassName('oj-ext-mobile-page-content')[0];
      const scrollableElement = this._nearestScrollableElement(contentElement);

      if (scrollableElement) {
        this._scrollElementToTop(scrollableElement, 300);
      }
    }

    /**
     * Scrolls an element to the top using ease in/out animation
     * https://stackoverflow.com/questions/21474678/scrolltop-animation-without-jquery
     * @param element the element to scroll to its 0 scrollTop position
     * @param duration duration in ms
     * @private
     */
    // eslint-disable-next-line class-methods-use-this
    _scrollElementToTop(element, duration) {
      const cosParam = element.scrollTop / 2;
      let scrollCount = 0;
      let oldTimestamp = window.performance.now();

      // step function for animation
      const stepFn = (newTimestamp) => {
        scrollCount += Math.PI / (duration / (newTimestamp - oldTimestamp));

        // As soon as we cross over Pi, we're about where we need to be
        if (scrollCount >= Math.PI) {
          // eslint-disable-next-line no-param-reassign
          element.scrollTop = 0;
        }

        if (element.scrollTop === 0) {
          return;
        }

        // eslint-disable-next-line no-param-reassign
        element.scrollTop = Math.round(0 + cosParam + cosParam * Math.cos(scrollCount));
        oldTimestamp = newTimestamp;
        window.requestAnimationFrame(stepFn);
      };
      // start the animation
      window.requestAnimationFrame(stepFn);
    }

    /**
     * returns the nearest scrollable child element of the content
     * @param element
     * @returns {HTMLElement|null}
     * @private
     */
    _nearestScrollableElement(element) {
      // use 2 px tolerence for borders
      if ((element.scrollHeight - element.clientHeight >= 2) && element.scrollTop > 0) {
        return element;
      }

      if (element.children.length > 0) {
        return this._nearestScrollableElement(element.children[0]);
      }

      return null;
    }
  }

  return PageTemplateComponentModel;
});

